import React, { useEffect } from "react";

import {
  TypeAuthContext,
  UserDadaType,
  sectionPermissionsTranslator,
  sectionsVisible,
} from "@context/auth/types";
import {
  deleteCookie,
  getCookie,
  setCookie,
  setYearCookie,
} from "@utils/utilsCookie";
import { customApi } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { cognitoApp, parseJwt, redirectToLearner } from "@utils/utilsCognito";
import {
  CognitoUserPool,
  CognitoUserSession,
  CookieStorage,
} from "amazon-cognito-identity-js";

interface GetUserData {
  sessionId: string;
  user: UserDadaType;
}

export const useAuthProvider = (): TypeAuthContext => {
  const [user, setUser] = React.useState<UserDadaType | undefined>(undefined);
  // const [cognitoUser, setCognitoUser] = React.useState<CognitoUserPool>();

  const redirectToInitiativeLogin = (initiativeUrl?: string) => {
    const initiativeUrlFromCookie = getCookie("initiativeUrl") || undefined;
    const initiativeTargetUrl = initiativeUrl || initiativeUrlFromCookie;
    if (!initiativeTargetUrl) return false;

    const initiativeBaseUrl = initiativeTargetUrl.startsWith("http")
      ? initiativeTargetUrl
      : `https://${initiativeTargetUrl}`;

    window.location.href = `${initiativeBaseUrl.replace(/\/+$/, "")}/access/login`;
    return true;
  };

  const getCognito = async (sessionId: string, initiativeUrl?: string) => {
    try {
      const response = await customApi<any | undefined>(
        ServicesURL.getSessionCognito.replace("{sessionId}", sessionId),
        MethodType.GET,
      );
      const resCognito = response?.data; // tolto { data: resCognito } in quanto se va in errore la chiamata non può fare destructuring

      if (
        resCognito &&
        resCognito.authToken &&
        resCognito.idToken &&
        resCognito.refreshToken
      ) {
        //setCookie("idToken", resCognito.idToken, new Date().getTime());
        setCookie("authToken", resCognito.authToken, new Date().getTime());
        // setCookie("refreshToken", resCognito.refreshToken, new Date().getTime());
      } else {
        redirectToLearner(
          "An error occured during GET /cognito/, response is not valid autToken, idToken or refreshToken is missing or expired",
        );
      }
      return resCognito;
    } catch (error: any) {
      console.log("Cognito catch error", error.message);
      const statusCode = error?.response?.status || error?.toJSON?.().status;
      if (statusCode === 401 && redirectToInitiativeLogin(initiativeUrl)) {
        return;
      }
      //TODO: Redirect to error page but not logged
      redirectToLearner(error);
    }
  };

  const getUsedDigited = async (
    sessionId: string,
    initiativeUrl: string | undefined,
  ) => {
    try {
      let resCognito: any;
      let cognitoUserReady = false;

      // Riusa la sessione Cognito dai cookie e salta GET /cognito; il match sul
      // sessionId evita di riusare la sessione di un altro utente.
      const boot = getCookie("cognitoBoot").split("|");
      if (boot.length === 3 && boot[2] === sessionId) {
        const [clientId, userpoolId] = boot;
        try {
          const existingUser = cognitoApp.restoreCognitoUserFromStorage(
            clientId,
            userpoolId,
            getCookie("corporate_domain") || "",
          );
          if (existingUser) {
            const session = await new Promise<CognitoUserSession>(
              (resolve, reject) => {
                existingUser.getSession((err: any, s: CognitoUserSession) => {
                  if (err || !s) reject(err || new Error("no session"));
                  else resolve(s);
                });
              },
            );
            resCognito = {
              clientId,
              userpoolId,
              idToken: session.getIdToken().getJwtToken(),
              authToken: session.getAccessToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken(),
            };
            cognitoUserReady = true;
          }
        } catch (e) {
          // sessione non riusabile: si procede con GET /cognito.
        }
      }

      if (!resCognito) {
        resCognito = await getCognito(sessionId, initiativeUrl);
        if (!resCognito) return;
      }

      // config pool + sessionId per il fast-path al prossimo reload.
      setCookie(
        "cognitoBoot",
        `${resCognito.clientId}|${resCognito.userpoolId}|${sessionId}`,
        1,
      );

      // Ricrea l'utente dai token di GET /cognito, cosi' un 401 puo' rinnovare
      // invece di redirigere alla disambiguazione.
      if (!cognitoUserReady) {
        let externalIdFromToken: string | undefined;
        try {
          externalIdFromToken = parseJwt(resCognito.idToken)?.[
            "custom:ExternalID"
          ];
        } catch (e) {
          externalIdFromToken = undefined;
        }
        if (externalIdFromToken) {
          try {
            cognitoApp.createCognitoUser(
              resCognito.clientId,
              resCognito.userpoolId,
              externalIdFromToken,
              resCognito.refreshToken,
              resCognito.authToken,
              resCognito.idToken,
              getCookie("corporate_domain") || "",
            );
            cognitoUserReady = true;
          } catch (e) {
            // fallback al blocco "tardivo" piu' sotto.
          }
        }
      }

      const { data: resUser } = await customApi<GetUserData | undefined>(
        ServicesURL.getSessionByID.replace("{sessionId}", sessionId),
        MethodType.GET,
      );

      if (resCognito && resUser) {
        const { data: permissions } = await customApi<string[]>(
          ServicesURL.getSelftPermissions,
          MethodType.GET,
        );
        const { data: corporate } = await customApi(
          `${ServicesURL.corporate}/${resUser.user.organization_id}`,
          MethodType.GET,
        );
        // Set Corporate domain cookie expires in 1 year not session
        setYearCookie(
          "corporate_domain",
          `https://${corporate.corporate_domain}`,
        );
        // Fallback: ricrea solo se non gia' fatto, per non scartare token freschi.
        if (!cognitoUserReady) {
          const expires = 0.3;
          const userPool = new CognitoUserPool({
            ClientId: resCognito.clientId,
            UserPoolId: resCognito.userpoolId,
            Storage: new CookieStorage({
              secure: true,
              domain: process.env.REACT_APP_DOMAIN_COGNITO,
              expires: expires,
            }),
          });
          userPool.getCurrentUser()?.signOut();
          cognitoApp.createCognitoUser(
            resCognito.clientId,
            resCognito.userpoolId,
            resUser.user.external_id,
            resCognito.refreshToken,
            resCognito.authToken,
            resCognito.idToken,
            `https://${corporate.corporate_domain}`,
          );
        }
        // Remove authToken because from now API calls
        // will get authToken from cognito user
        deleteCookie("authToken");
        if (resUser && resUser.user && permissions) {
          if (
            permissions.findIndex(
              (permission) => permission === "admin.dashboard.access",
            ) < 0
          ) {
            redirectToLearner(
              `An error occurred while checking permissions during session authentication, current user has not "admin.dashboard.access" permission`,
            );
            return;
          } else {
            let permissionList = permissions;
            let newPermissionList: any[] = [];

            permissionList &&
              permissionList.forEach((permission: string) => {
                // Get translator permissions for sections as declared inside useNavigation.ts
                if (sectionPermissionsTranslator[permission]) {
                  newPermissionList = newPermissionList.concat(
                    sectionPermissionsTranslator[permission],
                  );
                }
              });

            // Concat other always visible sections which don't require a permission
            if (permissionList)
              newPermissionList = [
                ...permissionList,
                ...newPermissionList,
                ...sectionsVisible,
              ];

            setUser({
              ...{
                ...resUser?.user,
                ...{
                  corporate_domain: `https://${
                    initiativeUrl ? initiativeUrl : corporate.corporate_domain
                  }`,
                },
              },
              ...resCognito,
              ...{
                permissions: [...newPermissionList],
              },
            });
          }
        } else {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (e: any) {
      console.log("Cognito catch error 2: ", e);
      const statusCode = e?.response?.status || e?.toJSON?.().status;
      if (statusCode === 401 && redirectToInitiativeLogin(initiativeUrl)) {
        return;
      }
      //TODO: Redirect to error page but not logged
      redirectToLearner(e);
    }
  };

  useEffect(() => {
    if (
      !window.location.href.includes("/ObsoletePage") &&
      !window.location.href.includes("/redirectError")
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get("sessionId") || getCookie("sessionId");
      const initiativeUrl = urlParams.get("initiativeUrl") || undefined;
      if (sessionId) {
        setCookie("sessionId", sessionId, new Date().getTime());
        initiativeUrl &&
          setCookie(
            "initiativeUrl",
            `https://${initiativeUrl}`,
            new Date().getTime(),
          );
        getUsedDigited(sessionId, initiativeUrl);
      } else {
        //TODO: Redirect to error page but not logged
        redirectToLearner("No sessionId by URL or cookie is provided");
      }
    }
  }, []);

  return {
    user,
    isLoading: false,
  };
};
